import React, { type RefObject, useCallback } from 'react';

import type { ContentProps } from '@atlaskit/popup/types';
import { getFirstFocusable } from '@atlaskit/top-layer/get-first-focusable';

import { FocusOnLoad, type FocusOnLoadProps } from './focus-on-load';

type FocusInsideOnLoadProps = Omit<FocusOnLoadProps, 'onLoad'> & {
	/**
	 * Ref holding the popup's own initial focus setter, taken from the `content`
	 * render props. It is a ref because the content element is created before
	 * the render props are available.
	 */
	setInitialFocusRef: RefObject<ContentProps['setInitialFocusRef'] | null>;
};

/**
 * Moves focus into the popup once the entry point has finished loading, so
 * makers don't have to manage focus manually after the loading fallback is
 * replaced by the final experience.
 */
export function FocusInsideOnLoad({
	entryPointReference,
	setInitialFocusRef,
}: FocusInsideOnLoadProps): React.JSX.Element {
	const handleLoad = useCallback(
		(container: HTMLElement) => {
			// Focus the first focusable element of the loaded experience, or the
			// popup itself when the experience has nothing focusable in it.
			const target = getFirstFocusable({ container }) ?? container;

			// The popup focuses its initial target once, when it opens. If that has
			// not happened yet — because the entry point was already loaded — this
			// makes the popup focus the target instead of itself, avoiding focus
			// moving twice.
			setInitialFocusRef.current?.(target);
			target.focus();
		},
		[setInitialFocusRef],
	);

	return <FocusOnLoad entryPointReference={entryPointReference} onLoad={handleLoad} />;
}
